<script lang="ts">
	import type { Favorite } from '$lib/components/profile/favorite/types';
	import type { WayPoint } from '$lib/components/search/itinerary/types';
	import { preloadData } from '$app/navigation';
	import { page } from '$app/state';
	import { CarEnergy, DeleteItinerariesFavoriteStore, ItineraryTrafficMode, ItineraryTravelMode } from '$houdini';
	import { computeSearchParamsItinerary } from '$lib/components/itinerary/util';
	import { addNotification } from '$lib/components/notifications/notifications';
	import { defaultNotificationCloseButtonProps } from '$lib/components/profile/favorite/constant';
	import ItinerariesFavoriteTable from '$lib/components/profile/favorite/ItinerariesFavoriteTable.svelte';
	import { ROUTE_RESULTS } from '$lib/constants/route';
	import { i18nPath } from '$lib/i18n/routing';
	import { trackDeleteFavorites, trackDisplayFavorites } from '$lib/services/tracking/googleAnalytics/favorites';
	import { getSearchParamsStore, type SearchParamsType, serializeSearchParams } from '$lib/services/urls/searchParams';
	import { useCarStorage } from '$lib/utils/carStorage.svelte';
	import * as m from '$paraglide/messages';
	import { languageTag } from '$paraglide/runtime';

	const { userProfileStore } = page.data;
	const isLoading = $derived($userProfileStore?.fetching || !$userProfileStore?.data);
	let favorites = $state($userProfileStore?.data?.me?.itinerariesFavorites ?? []) as Favorite[];
	const searchParams = getSearchParamsStore();
	const { getCarInfo } = useCarStorage();

	let deleteItinerariesFavoriteStore: DeleteItinerariesFavoriteStore;

	function onMutationSuccess(updatedFavorites: Favorite[], deletedFavorite: Favorite) {
		addNotification({
			message: m.bookmark__iti_deleted__success(),
			closeButton: {
				...defaultNotificationCloseButtonProps,
				text: m.modal__close(),
			},
		});
		trackDeleteFavorites(updatedFavorites.length, deletedFavorite);
		favorites = updatedFavorites;
	}

	function onMutationError() {
		addNotification({
			message: m.favoriteItineraries__deleteError(),
			closeButton: {
				...defaultNotificationCloseButtonProps,
				text: m.modal__close(),
			},
		});
	}

	async function onDeleteFavorite(timestamp: number) {
		if (!deleteItinerariesFavoriteStore) {
			deleteItinerariesFavoriteStore = new DeleteItinerariesFavoriteStore();
		}

		const optimisticUpdatedFavorites = $userProfileStore?.data?.me?.itinerariesFavorites?.filter((favorite: Favorite) => favorite.timestamp !== timestamp) ?? [];
		const deletedFavorite = $userProfileStore!.data!.me!.itinerariesFavorites!.find((favorite: Favorite) => favorite.timestamp === timestamp)! as Favorite;

		try {
			const result = await deleteItinerariesFavoriteStore.mutate({
				input: {
					timestamp,
				},
			}, {
				optimisticResponse: {
					deleteItinerariesFavorite: {
						itinerariesFavorites: optimisticUpdatedFavorites,
					},
				},
			});

			if (!result.errors && result.data?.deleteItinerariesFavorite) {
				onMutationSuccess(result.data.deleteItinerariesFavorite.itinerariesFavorites as Favorite[], deletedFavorite);
			} else {
				onMutationError();
			}
		} catch (error) {
			console.error(`Error deleting favorite ${timestamp}:`, error);
			onMutationError();
		}
	}

	async function onComputeItinerary(favorite: Favorite) {
		try {
			const {
				departureName,
				arrivalName,
				coordinates,
				isEv,
				steps,
				withCaravan,
				mode,
				constraints,
				distanceSystem,
				currency,
				carId,
			} = favorite;

			trackDisplayFavorites(favorites.length, favorite);

			const waypoints = [
				{ label: departureName, index: 0, id: 0 },
				...steps.map((step, index) => ({ label: step, index: index + 1, id: index + 1 })),
				{ label: arrivalName, index: steps.length + 1, id: steps.length + 1, isArrival: true },
			].map(wp => ({ ...wp, coordinates: coordinates[wp.index] })) satisfies WayPoint[];
			const searchParamsItinerary = await computeSearchParamsItinerary(waypoints);

			const carInfo = getCarInfo(carId) ?? { model: '', maker: '', isEv: false, energy: CarEnergy.GASOLINE };

			const sP = {
				...$searchParams,
				from: departureName,
				to: arrivalName,
				itinerary: searchParamsItinerary,
				travelMode: mode,
				tripConstraint: constraints,
				traffic: ItineraryTrafficMode.CLOSINGS,
				car: mode === ItineraryTravelMode.CAR
					? {
						isDefault: false,
						id: carId,
						...carInfo,
					}
					: undefined,
				withCaravan: mode === ItineraryTravelMode.CAR ? withCaravan : undefined,
				selectedRoute: 0,
				zoiSettings: {
					...$searchParams.zoiSettings,
					containsEvOnly: isEv,
				},
				currency,
				distanceSystem,
				isEv,
			} satisfies SearchParamsType;

			const search = serializeSearchParams(sP);
			// Hackish way to load the itinerary results page to prevent vector map not loading on programmatic navigation
			// Event using a link with data-sveltekit-reload does not work
			// We need to create a way to load the map correctly when we are navigating from pages outside the (map) layout
			const url = i18nPath(`${ROUTE_RESULTS}?${search}`, languageTag());
			await preloadData(url);
			window.location.href = url;
		} catch (error) {
			console.error(`Error computing itinerary for favorite ${favorite.timestamp}:`, error);
			addNotification(m.search_tools__messages__error());
		}
	}
</script>

<div class="flex flex-col gap-4 size-full">
	<h1 class="hidden md:block font-size-5 font-600 line-height-7 text-coloredneutral m-0">{m.bookmark__favorite__routes()}</h1>
	<ItinerariesFavoriteTable
		{favorites}
		{isLoading}
		{onDeleteFavorite}
		{onComputeItinerary}
	/>
</div>
